import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Alert,
  InputAdornment,
  Select,
  Button,
  MenuItem,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";

import ComponentAbstract from "components/ComponentAbstract";
import i18n from "i18";
import config from "config";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import _request from "actions/_request";
import AppContext from "../AppContext";

import "../styles/index.css";

import ThemeSwitcher from "../ThemeSwitcher";
import { takeReturnTo } from "../utils/returnTo";

export default class Login extends ComponentAbstract {
  static contextType = AppContext;

  static propTypes = {
    navigate: PropTypes.func,
    hideHeader: PropTypes.bool,
  };

  static defaultProps = {
    navigate: null,
    hideHeader: false,
  };

  constructor(props) {
    super(props);

    this.titles = {
      rightTitle: "signup",
      mainTitle: "loginMain",
      smallTitle: "loginSmall",
      signupMain: "signupMain",
    };

    this.state = {
      ...this.state,
      email: "",
      password: "",
      error: {},
      showPass: false,
      rememberMe: false,
      emailSent: false,
      errorMessage: "",
      needsVerification: false,
      activationSent: false,
    };

    this.emailReg =
      /^[A-Za-z0-9](([_.\-]?[a-zA-Z0-9]+)*)@([A-Za-z0-9]+)(([.\-]?[a-zA-Z0-9]+)*)\.([A-Za-z]{2,})$/g;
    this.emailInvalidText = this.tr("emailInvalid");

    localStorage.token = "";
  }

  validateEmail(value) {
    if (!value.match(this.emailReg)) {
      if (this.state.error.key !== "email")
        this.setState({
          error: {
            key: "email",
            text: this.emailInvalidText,
            validator: this.validateEmail.bind(this),
          },
        });

      return false;
    }

    return true;
  }

  isButtonDisabled() {
    const { email, password } = this.state;

    return !(email.length > 5 && password.length > 2);
  }

  changeInputValue(key, value) {
    let { error } = this.state;

    if (error.key === key && error.validator(value)) error = {};

    this.setState({ [key]: value, error });
  }

  onInputEnter() {
    if (!this.isButtonDisabled()) this.mainButtonClick();
  }

  tr(key) {
    return i18n.t(`auth.${key}`);
  }

  async mainButtonClick() {
    const { email, password, rememberMe } = this.state;

    if (!this.validateEmail(email)) return false;

    const resp = await _request.auth.loading(async () => {
      return await _request.auth.login({
        email,
        password,
        remember: rememberMe,
      });
    });

    if (resp?.isOk) return this.loginOk();

    // The account exists but never confirmed its email. Without the resend option below
    // there is no way out of that state — the activation link is only mailed at signup.
    this.setState({
      needsVerification: resp?.data?.reason === "email_not_verified",
      activationSent: false,
    });
  }

  async resendActivation() {
    const { email } = this.state;

    const resp = await _request.auth.loading(async () => {
      return await _request.auth.resendActivation({ email });
    });

    if (resp?.isOk) this.setState({ activationSent: true });
  }

  loginOk() {
    // Continue to wherever the visitor was going before we asked them to sign in
    // (a link from the reconnect email, say); otherwise the dashboard as before.
    const returnTo = takeReturnTo();

    this.context.loadMetaData(() => {
      this.props.navigate(returnTo || "/app");
    }, true);
  }

  insertInput({ key, label, placeholder, icon, props }) {
    const { error } = this.state;
    const isError = error.key === key;

    const input = this.insertGlobalInput(
      {
        inputRef: (el) => isError && el && el.focus(),
        placeholder: this.tr(placeholder),
        error: isError,
        helperText: isError ? error.text : "",
        onKeyUp: this.changeInputValue.bind(this, key),
        InputProps: {
          startAdornment: (
            <InputAdornment position="start">{icon}</InputAdornment>
          ),
          sx: {
            height: "48px",
            backgroundColor: "var(--background)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: "var(--border-color)",
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: "var(--accent-primary)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--accent-primary)",
            },
            "& input": {
              color: "var(--text-color)",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "15px",
              fontWeight: "500",
            },
          },
        },
        sx: {
          "& .MuiInputAdornment-root": {
            color: "var(--text-secondary)",
          },
        },
        ...props,
      },
      {
        onEnter: this.onInputEnter.bind(this),
      }
    );

    if (!label) return input;

    return (
      <Box className="flex column" sx={{ gap: "6px" }}>
        <Typography 
          className="font w600" 
          sx={{ 
            color: "var(--text-color)", 
            fontSize: "14px",
            fontWeight: "600"
          }}>
          {this.tr(label)}
        </Typography>
        {input}
      </Box>
    );
  }

  insertEmail(
    key,
    label = "emailAddress",
    placeholder = "emailDetailed",
    autoFocus = true
  ) {
    return this.insertInput({
      key,
      label,
      placeholder: placeholder || label,
      icon: <Box className="messageIcon" />,
      props: {
        type: "email",
        autoFocus,
      },
    });
  }

  insertPassword(key, label = "password", placeholder = "passwordDetailed") {
    const { showPass } = this.state;
    const IconRight = showPass ? VisibilityIcon : VisibilityOffIcon;
    const type = showPass ? "text" : "password";

    return this.insertInput({
      key,
      label,
      placeholder: placeholder || label,
      icon: (
        <>
          <Box className="hand" />
          <IconRight
            className="eyeIcon"
            sx={{ 
              color: "var(--text-secondary)",
              cursor: "pointer",
              transition: "color 0.2s ease",
              "&:hover": {
                color: "var(--accent-primary)",
              }
            }}
            onClick={this.changeState.bind(this, "showPass", !showPass)}
          />
        </>
      ),
      props: {
        type,
      },
    });
  }

  insertSelect(value, items, cb) {
    return (
      <Select
        sx={{
          backgroundColor: "var(--background)",
          color: "#9FA3BD",
          borderBottom: "2px solid rgb(159, 163, 189)",
          borderRadius: "7px",
        }}
        value={value}
        onChange={cb}
        variant="standard">
        {items.map((it, i) => (
          <MenuItem key={i} value={it.id}>
            {it.name}
          </MenuItem>
        ))}
      </Select>
    );
  }

  insertButton(text) {
    return (
      <Button className="buttonNew" onClick={this.mainButtonClick.bind(this)}>
        {this.tr(text)}
      </Button>
    );
  }
  async googleButtonClick(credentialResponse) {
    const resp = await _request.auth.loading(async () => {
      return await _request.auth.google({ id_token: credentialResponse.credential });
    });

    if (resp?.isOk) this.loginOk();
  }

  async appleButtonClick() {
    try {
      window.AppleID.auth.init({
        clientId: config.appleClientId,
        scope: "name email",
        redirectURI: window.location.origin,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();
      const body = { id_token: response.authorization.id_token };

      if (response.user) {
        body.full_name = `${response.user.name.firstName} ${response.user.name.lastName}`.trim();
      }

      const resp = await _request.auth.loading(async () => {
        return await _request.auth.apple(body);
      });

      if (resp?.isOk) this.loginOk();
    } catch (error) {
      if (error?.error !== "popup_closed_by_user") {
        console.error("Apple sign-in failed:", error);
        globalThis.appView?.alertError(i18n.t("auth.socialAuthError"));
      }
    }
  }

  insertSocialButtons() {
    return (
      <Box className="flex column" sx={{ gap: "12px" }}>
        <Box className="socialDivider">
          <span>{this.tr("or")}</span>
        </Box>
        <Box className="flex justify-center w-full">
          <GoogleLogin
            onSuccess={this.googleButtonClick.bind(this)}
            onError={() => {}}
            width="400"
            theme="outline"
            size="large"
            text="continue_with"
            shape="rectangular"
          />
        </Box>
        <Box className="flex justify-center w-full">
          <Button
            className="buttonApple"
            onClick={this.appleButtonClick.bind(this)}>
            <div className="appleIcon" />
            {this.tr("continueApple")}
          </Button>
        </Box>
      </Box>
    );
  }

  handleCheckboxChange(event) {
    this.setState({ rememberMe: event.target.checked });
  }

  renderVerificationNotice() {
    const { needsVerification, activationSent } = this.state;

    if (!needsVerification) return null;

    if (activationSent) {
      return <Alert severity="success">{this.tr("activationResent")}</Alert>;
    }

    return (
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={this.resendActivation.bind(this)}>
            {this.tr("resendActivation")}
          </Button>
        }>
        {this.tr("notVerified")}
      </Alert>
    );
  }

  renderForm() {
    return (
      <Box className="flex column" sx={{ gap: "20px" }}>
        {this.renderVerificationNotice()}
        {this.insertEmail("email")}
        {this.insertPassword("password")}
        <Box className="flex between centerV">
          <Box className="flex gap6 centerV">
            <Checkbox
              className="checkbox"
              checked={this.state.rememberMe}
              onChange={this.handleCheckboxChange.bind(this)}
              sx={{
                color: "var(--text-secondary)",
                padding: "4px",
                "&.Mui-checked": {
                  color: "var(--accent-primary)",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "22px",
                },
              }}
            />
            <Typography
              className="checkboxText"
              component="span"
              sx={{ 
                color: "var(--text-color)",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                userSelect: "none"
              }}
              onClick={() => this.setState({ rememberMe: !this.state.rememberMe })}>
              {this.tr("rememberMe")}
            </Typography>
          </Box>
          <Link to="/auth/forgot" className="forgot">
            {this.tr("forgotPass") + "?"}
          </Link>
        </Box>
        <Box>{this.insertButton("login")}</Box>
        {this.insertSocialButtons()}
      </Box>
    );
  }

  render() {
    const { hideHeader } = this.props;

    if (hideHeader) {
      return this.renderForm();
    }

    const { mainTitle, smallTitle, rightTitle } = this.titles;

    return (
      <Box className="flex1" sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box 
          className="flex between centerV" 
          sx={{ 
            height: "32px",
            minHeight: "32px",
            maxHeight: "32px",
            alignItems: "center",
            overflow: "hidden"
          }}>
          <div
            className="authPage newLogo cursor-pointer"
            onClick={() => (window.location.href = "/")}></div>
          <Box sx={{ flexShrink: 0 }}>
            <ThemeSwitcher />
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography className="mainTitle">{this.tr(mainTitle)}</Typography>
          <Typography className="smallTitle">
            {this.tr(smallTitle)}
            <Link to={`/auth/${rightTitle}`} className="rightTitle">
              {this.tr(rightTitle) + " !"}
            </Link>
          </Typography>
        </Box>
        
        {this.renderForm()}
      </Box>
    );
  }
}
